import { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import useGetGeofencesList from '../../API/hooks/geofences/useGetGeofencesList';
import usePostGeofence from '../../API/hooks/geofences/usePostGeofence';
import useDeleteGeofence from '../../API/hooks/geofences/useDeleteGeofence';
import usePatchGeofence from '../../API/hooks/geofences/usePatchGeofence';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import GeofenceMap from '../../components/common/Map/Geofence/GeofenceMap';
import Tags from '../../components/common/Tags';
import useGetRole from '../../API/hooks/useGetRole';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import { Button } from '../../components/ui/Button/Button';
import Input from '../../components/common/Input/Input';
import TextArea from '../../components/common/TextArea/TextArea';
import {
  convertCircleToPolygon, isCirclePolygon, getCircleParams, hasExactlyFiveDecimals,
  formatCoordinateToFiveDecimals, getPolygonValidationError,
} from '../../components/common/Map/helpers';
import { MIN_GEOFENCE_RADIUS } from '../../components/common/Map/constants';
import S from './GeofencesPageStyles';
import { Geofence } from '../../API/types/Geofences';
import Modal from '../../components/common/Modal/Modal';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import { ThemeType } from '../../theme/themeType';

const coordinateRegex = /^\s*(?:-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*(?:\n|$))+\s*$/;

const parsePolygonCoordinates = (input: string): number[][] => {
  const lines = input.split('\n').filter((line) => line.trim() !== '');

  const coordinates: number[][] = lines.map((line) => {
    const [latStr, lonStr] = line.split(',').map((part) => part.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    return [lon, lat];
  });

  return coordinates;
};

function GeofencesPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const fleetId = useGetFleetId();
  const { data: geofencesList } = useGetGeofencesList(fleetId);
  const { isFleetViewer } = useGetRole();
  const navigate = useNavigate();
  const [editGeofence, setEditGeofence] = useState<Geofence | null>(null);
  const [geofenceToDelete, setGeofenceToDelete] = useState<Geofence | null>(null);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitFail = (errorMessage: string): void => {
    toast.error(errorMessage);
  };
  const { mutate: createGeofence } = usePostGeofence(
    (): void => {
      setSubmitError('');
      toast.success('Geofence created successfully');
    },
    onSubmitFail,
  );

  const { mutate: deleteGeofence } = useDeleteGeofence(
    (): void => { toast.success('Geofence deleted successfully'); },
    onSubmitFail,
  );

  const { mutate: updateGeofence } = usePatchGeofence(
    (): void => { toast.success('Geofence updated successfully'); },
    onSubmitFail,
  );

  if (isFleetViewer) {
    navigate('/home');
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      lat: '',
      lon: '',
      radius: '',
      geometryType: 'Circle',
      polygonCoordinates: [] as number[][],
      polygonCoordinatesText: '',
    },
    onSubmit: (values) => {
      const polygon = values.geometryType === 'Circle' ? convertCircleToPolygon(
        { lat: parseFloat(values.lat), lng: parseFloat(values.lon) },
        parseInt(values.radius, 10),
      ) : values.polygonCoordinates;
      const geofenceData = {
        fleetId,
        name: values.name,
        location: {
          type: 'Polygon',
          coordinates: [polygon],
        },
      };
      if (!editGeofence) {
        createGeofence(geofenceData);
      } else {
        updateGeofence({ id: editGeofence._id, ...geofenceData });
        setEditGeofence(null);
      }
      formik.resetForm();
    },
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.name?.trim()) errors.name = 'Name can\'t be empty';

      if (values.geometryType === 'Circle') {
        if (!values.lat.trim()) {
          errors.lat = 'Latitude can\'t be empty';
        } else if (!hasExactlyFiveDecimals(values.lat)) {
          errors.lat = 'Exactly 5 decimals needed';
        } else {
          const lat = Number(values.lat);

          if (Number.isNaN(lat)) {
            errors.lat = 'Must be a valid number';
          } else if (lat < -90 || lat > 90) {
            errors.lat = 'Must be between -90 and 90';
          }
        }

        if (!values.lon.trim()) {
          errors.lon = 'Longitude can\'t be empty';
        } else if (!hasExactlyFiveDecimals(values.lon)) {
          errors.lon = 'Exactly 5 decimals needed';
        } else {
          const lon = Number(values.lon);

          if (Number.isNaN(lon)) {
            errors.lon = 'Must be a valid number';
          } else if (lon < -180 || lon > 180) {
            errors.lon = 'Must be between -180 and 180';
          }
        }
        if (!values.radius?.trim()) {
          errors.radius = 'Radius can\'t be empty';
        } else if (!/^\d+$/.test(values.radius.trim())) {
          errors.radius = 'Radius must be a whole number';
        } else if (Number(values.radius) < MIN_GEOFENCE_RADIUS) {
          errors.radius = `Radius must be at least ${MIN_GEOFENCE_RADIUS}m`;
        }
      } else if (values.geometryType === 'Polygon') {
        if (!values.polygonCoordinatesText?.trim()) {
          errors.polygonCoordinatesText = 'Polygon coordinates can\'t be empty';
        }
        if (values.polygonCoordinates.length < 4) {
          errors.polygonCoordinatesText = 'A polygon must have at least 3 points';
        }
        if (!coordinateRegex.test(values.polygonCoordinatesText)) {
          errors.polygonCoordinatesText = 'Invalid format. Expected lines of "lat, lon"';
        }

        const coordinates = values.polygonCoordinates;
        if (coordinates.length >= 4) {
          const firstCoord = coordinates[0];
          const lastCoord = coordinates[coordinates.length - 1];

          if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
            errors.polygonCoordinatesText = 'Polygon must be closed (first and last points must match)';
          } else {
            const polygonValidationError = getPolygonValidationError(coordinates);
            if (polygonValidationError) {
              errors.polygonCoordinatesText = polygonValidationError;
            }
          }
        }
      }

      return errors;
    },
    validateOnMount: true,
  });

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
  };

  const handlePolygonInputChange = (e: ChangeEvent<any>): void => {
    const { name, value } = e.target;
    formik.handleChange(e);

    if (name === 'polygonCoordinatesText') {
      const coordinates = parsePolygonCoordinates(value);
      formik.setFieldValue('polygonCoordinates', coordinates);
    }
  };

  const handleCircleUpdate = (circle: google.maps.Circle): void => {
    const center = circle?.getCenter();

    formik.setValues({
      name: formik.values.name,
      lat: center ? formatCoordinateToFiveDecimals(center.lat()) : '',
      lon: center ? formatCoordinateToFiveDecimals(center.lng()) : '',
      radius: Math.round(circle?.getRadius())?.toString() || '',
      geometryType: 'Circle',
      polygonCoordinates: [],
      polygonCoordinatesText: '',
    });
  };

  const handlePolygonUpdate = (polygon: google.maps.Polygon): void => {
    const coordinates = polygon?.getPath()?.getArray()?.map((coord) => [+coord.lng().toFixed(10), +coord.lat().toFixed(10)]);
    coordinates?.push(coordinates[0]);
    const coordinateLines = coordinates?.map(
      (coord: number[]) => `${coord[1]}, ${coord[0]}`,
    );
    formik.setValues({
      name: formik.values.name,
      lat: '',
      lon: '',
      radius: '',
      geometryType: 'Polygon',
      polygonCoordinates: coordinates,
      polygonCoordinatesText: coordinateLines?.join('\n') || '',
    });
  };

  const handleEditClick = (geofence: Geofence): void => {
    setEditGeofence(geofence);
    const isCircle = isCirclePolygon(geofence);
    if (isCircle) {
      const { center, radius } = getCircleParams(geofence);
      formik.setValues({
        name: geofence?.name || '',
        lat: formatCoordinateToFiveDecimals(center.lat),
        lon: formatCoordinateToFiveDecimals(center.lng),
        radius: radius?.toString() || '',
        geometryType: 'Circle',
        polygonCoordinates: [],
        polygonCoordinatesText: '',
      });
    } else {
      const geofenceCoordinates = geofence?.location?.coordinates?.[0] || [];
      const coordinateLines = geofenceCoordinates.map(
        (coord: number[]) => `${coord[0]}, ${coord[1]}`,
      );
      formik.setValues({
        name: geofence.name || '',
        lat: '',
        lon: '',
        radius: '',
        geometryType: 'Polygon',
        polygonCoordinates: geofenceCoordinates,
        polygonCoordinatesText: coordinateLines.join('\n'),
      });
    }
  };

  const handleDeleteClick = (geofence: Geofence): void => {
    setGeofenceToDelete(geofence);
    toggleDeleteModalOpen(true);
  };

  const handleDeleteGeofence = (): void => {
    if (geofenceToDelete === null) return;
    deleteGeofence({ id: geofenceToDelete._id, fleetId });
    setGeofenceToDelete(null);
    setEditGeofence(null);
    toggleDeleteModalOpen(false);
    formik.resetForm();
  };

  const handleCancelClick = (): void => {
    setEditGeofence(null);
    formik.resetForm();
  };

  return (
    <>
      <S.Wrapper>
        <div className="widgets-container">
          <div className="geofence-management">
            <form onSubmit={formik.handleSubmit}>
              <S.RowContainer>
                <p>Figure:</p>
                <input
                  type="radio"
                  id="figureCircle"
                  name="geometryType"
                  value="Circle"
                  onChange={handleInputChange}
                  checked={formik.values.geometryType === 'Circle'}
                />
                <label htmlFor="figureCircle"><span className="checkbox" />Circle</label>
                <input
                  type="radio"
                  id="figurePolygon"
                  name="geometryType"
                  value="Polygon"
                  onChange={handleInputChange}
                  checked={formik.values.geometryType === 'Polygon'}
                />
                <label htmlFor="figurePolygon"><span className="checkbox" />Polygon</label>
              </S.RowContainer>
              <S.RowContainer>
                <Input
                  label="Name:"
                  name="name"
                  placeholder="Name"
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  error={formik.touched.name && formik.errors.name}
                />
              </S.RowContainer>
              {formik.values.geometryType === 'Circle' && (
                <>
                  <S.RowContainer>
                    <Input
                      label="Lat:"
                      name="lat"
                      placeholder="lat:"
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.lat}
                      error={formik.touched.lat && formik.errors.lat}
                    />
                    <Input
                      label="Lon:"
                      name="lon"
                      placeholder="Lon:"
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.lon}
                      error={formik.touched.lon && formik.errors.lon}
                    />
                  </S.RowContainer>
                  <S.RowContainer>
                    <Input
                      label="Radius:"
                      name="radius"
                      placeholder="Radius (m)"
                      min={MIN_GEOFENCE_RADIUS}
                      step={1}
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.radius}
                      error={formik.touched.radius && formik.errors.radius}
                    />
                  </S.RowContainer>
                </>
              )}
              {formik.values.geometryType === 'Polygon' && (
                <S.RowContainer>
                  <TextArea
                    name="polygonCoordinatesText"
                    rows={4}
                    placeholder="Enter coordinates as:
lat1, lon1
lat2, lon2
lat3, lon3"
                    value={formik.values.polygonCoordinatesText}
                    onChange={handlePolygonInputChange}
                    onBlur={() => formik.setFieldTouched('polygonCoordinatesText', true)}
                    error={formik.touched.polygonCoordinatesText && formik.errors.polygonCoordinatesText}
                  />
                </S.RowContainer>
              )}
              <div className="buttons-container">
                <Button
                  height="50px"
                  onClick={handleCancelClick}
                  type="button"
                  background={editGeofence ? theme.colors.green : ''}
                >
                  Cancel
                </Button>
                <Button
                  className="add-button"
                  height="50px"
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {editGeofence ? 'Update' : 'Create'}
                </Button>
              </div>
              {!!submitError && (
                typeof submitError === 'string'
                  ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
                  : submitError.map((e) => <S.ErrorMessage>{e}</S.ErrorMessage>)
              )}
            </form>
            <div className="geofence-list">
              {geofencesList?.results?.map((geofence) => (
                <div className="geofence-item" key={geofence._id}>
                  <Tags.Label label={geofence.name} />
                  {(editGeofence?._id === geofence._id) && <Tags.Status label="is editing" backgroundColor="red" />}
                  {!isFleetViewer && (
                    <div className="button-group">
                      {(editGeofence?._id !== geofence._id) && (
                        <Button
                          height="44px"
                          padding="10px 12px"
                          title="Edit"
                          onClick={() => handleEditClick(geofence)}
                        >
                          <Icon iconKey={IconKeys.EDIT} />
                        </Button>
                      )}
                      <Button
                        height="44px"
                        padding="10px 12px"
                        title="Delete"
                        onClick={() => handleDeleteClick(geofence)}
                      >
                        <Icon iconKey={IconKeys.DELETE} />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <GeofenceMap
            currentGeofence={formik.values}
            geofencesList={geofencesList}
            onCircleComplete={handleCircleUpdate}
            onPolygonComplete={handlePolygonUpdate}
          />
        </div>
      </S.Wrapper>
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          onClose={() => setGeofenceToDelete(null)}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this geofence?"
            onClose={() => {
              toggleDeleteModalOpen(false);
              setGeofenceToDelete(null);
            }}
            onDelete={handleDeleteGeofence}
          />
        </Modal>
      )}
    </>
  );
}

export default GeofencesPage;
